import { useState, useEffect } from "react";
import {
    Box,
    Paper,
    Typography,
    ListItem,
    ListItemButton,
    ListItemText,
    FormControl,
    Select,
    MenuItem,
    InputLabel
} from "@mui/material";
import LoadingCircle from "../../../components/LayoutComponents/LoadingCircle";
import CourseForm from "./CourseForm";
import { createCourse, updateCourse, deleteCourse, requestCourseTypes, getCourseSkills, updateCourseSkills, requestCourses } from "../../../remote/courseRequest";
import theme from "../../../components/LayoutComponents/theme";

export default function Courses({ session, competencies, proficiencies, courses, setCourses, companies }) {
    const [selected, setSelected] = useState(null);
    const [skills, setSkills] = useState([]);
    const [courseTypes, setCourseTypes] = useState(null);
    const [selectedCourseType, setSelectedCourseType] = useState("");
    const [selectedSchool, setSelectedSchool] = useState("");
    const initialFormData = {
        courseId: 0,
        active: false,
        assessmentMethodId: null,
        clinicalHours: 0,
        contactHours: 0,
        courseTypeId: null,
        creditHours: 0,
        deliveryFormatId: null,
        entityId: null,
        featured: false,
        lectureHours: 0,
        owner: null,
        teachingMethodId: null,
        courseCode: "",
        courseName: "",
        courseContact: "",
        linkAdditionalInformation: "",
        courseDescription: "",
        courseOutline: "",
        courseRequirements: "",
        learningObjectives: "",
        courseEnrollmentType: "",
        effectiveEndDate: null,
        effectiveStartDate: null
    };

    const [formData, setFormData] = useState(initialFormData);
    const [loading, setLoading] = useState(false);


    useEffect(() => {
        if (!session?.token) return;
        const getCourseTypes = async () => {
           
                setLoading(true);
                const res = await requestCourseTypes(session?.token);
                setCourseTypes(res.data || []);
                setLoading(false);
        };
        if (!courseTypes) 
            getCourseTypes();
    }, [session?.token]);

    useEffect(() => {
        if (!session?.token) return;
        const loadSkills = async () => {
           
                setLoading(true);
                const response = await getCourseSkills(session?.token, selected?.courseId);
                setSkills(response.data || []);
                setLoading(false);
        };

         if (selected?.courseId > 0) 
            loadSkills();
    }, [session?.token, selected?.courseId]);

    const handleSave = async () => {
        let res;
        let resSkills;
        try {
            setLoading(true);
            if (!formData?.courseId || formData?.courseId === 0) {
                res = await createCourse(session?.token, formData);
                resSkills = await getCourseSkills(res.data.courseId)
                setCourses((prev) => [res.data, ...prev]);
                setSkills(resSkills.data);
            } else {
                await updateCourse(session?.token, formData);
                await updateCourseSkills(session?.token, selected?.courseId, skills);
                res = await requestCourses(session?.token)
                setCourses(res.data || [])
            }
        } catch (err) {
            console.error("Error saving course.", err);
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (profile) => {
        try {
            setLoading(true);
            let res = await deleteCourse(session?.token, selected?.courseId);
            setCourses(res.data);
            setSelected(null);
            setFormData(initialFormData);
        } catch (err) {
            console.error("Error deleting course.", err);
        } finally {
            setLoading(false);
        }
    };

    return (
        <Box sx={{ display: "flex", gap: 3, width: "100%", alignItems: "flex-start" }}>
            <Paper
                sx={{
                    width: "25%",
                    p: 2,
                    height: "60vh",
                    overflowY: "auto",
                }}
            >
                <Typography
                    variant="h6"
                    sx={{ mb: 2, color: theme.colors.purple, textAlign: "center" }}
                >
                    Courses
                </Typography>
                <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                    <InputLabel>Filter by Education Facility</InputLabel>
                    <Select
                        value={selectedSchool ?? ""}
                        label="Filter by Company"
                        onChange={(e) => setSelectedSchool(Number(e.target.value))}
                    >
                        <MenuItem value="">All</MenuItem>
                        {companies.map((company) => (
                            <MenuItem key={company.entityId} value={company.entityId}>
                                {company.entityName}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
                <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                    <InputLabel>Filter by Course Type</InputLabel>
                    <Select
                        value={selectedCourseType ?? ""}
                        label="Filter by Skill Profile"
                        onChange={(e) => setSelectedCourseType(Number(e.target.value))}
                    >
                        <MenuItem value="">All</MenuItem>
                        {courseTypes?.map((type) => (
                            <MenuItem key={type.courseTypeId} value={type.courseTypeId}>
                                {type.courseTypeName}   
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
                {courses?.length > 0 || !loading ?
                    courses
                        ?.filter(t => !selectedCourseType || t.courseTypeId === selectedCourseType)
                        ?.filter(t => !selectedSchool || t.entityId === selectedSchool)
                        ?.sort((a, b) => a.courseName.toLowerCase().localeCompare(b.courseName.toLowerCase()))                        
                        .map((course) => (
                            <ListItem key={course.courseId} disablePadding>
                                <ListItemButton
                                    selected={selected?.courseId === course.courseId}
                                    onClick={() => {
                                        setSelected(course);
                                        setFormData(course);
                                    }}
                                >
                                    <ListItemText primary={course?.courseName} />
                                </ListItemButton>
                            </ListItem>
                        )) : <LoadingCircle size={20} />}
            </Paper>
            <Box sx={{ flex: 1, minWidth: 600, minHeight: "60vh" }}>
                <CourseForm
                    onSave={handleSave}
                    onDelete={handleDelete}
                    session={session}
                    formData={formData}
                    setFormData={setFormData}
                    loading={loading}
                    competencies={competencies}
                    proficiencies={proficiencies}
                    companies={companies}
                    skills={skills}
                    setSkills={setSkills}
                />
            </Box>
        </Box>
    );
}